import { clamp01 } from "../math.js";
import { FACE_EDGE, faceEdgeKeep } from "./face-edge.js";
import { radiusScale, thinPlateSpline, type Point } from "./thin-plate.js";

// The X-ray ships as one byte for each cell of the skeleton's dot grid: 0 for no dot, else
// the dot's radius in `XRAY_RADIUS_LEVELS` steps. Only the bright dots are in it, and each
// one's size carries how bright it was, so the page can draw it in one color. The page moves
// the dots onto the face here, so the file stays the skeleton's own grid and stays small.

/** The skeleton's grid: this many cells on each side. */
export const XRAY_GRID = 158;
/** The grid's pitch, in the skeleton's units. A cell's center is `XRAY_STEP * index + 4`. */
export const XRAY_STEP = 8;
/** The radius of a full byte, in the skeleton's units. The largest dot is 3.71. */
export const XRAY_MAX_RADIUS = 4.5;
/** How many sizes a dot can take. At the portrait's size, 15 looks the same as 255. */
export const XRAY_RADIUS_LEVELS = 15;

export interface Landmark {
  name: string;
  /** On the skeleton, in its own units. */
  xray: Point;
  /** On the face, in the face artwork's units (1024 on each side). */
  face: Point;
}

// The skeleton is turned further from you than the face, and its face is shorter, so no rigid
// or perspective transform lines them up. These pairs put a point of the skeleton under the
// point of the face over it. The face wears sunglasses, so an eye socket goes to its lens's
// center. The skull's top sits just inside the scalp, and the nasal opening behind the nose's
// base. The teeth and the chin keep the skull's own shape, which puts the jaw forward, inside
// the beard. The collarbones' outer ends go under the shirt's shoulders, and the notch
// between them at the neckline.
export const XRAY_LANDMARKS: readonly Landmark[] = [
  { name: "top of the skull", xray: { x: 742, y: 68 }, face: { x: 584, y: 41 } },
  { name: "back of the skull", xray: { x: 1097, y: 418 }, face: { x: 881, y: 338 } },
  { name: "far eye socket", xray: { x: 449, y: 376 }, face: { x: 310, y: 366 } },
  { name: "near eye socket", xray: { x: 697, y: 429 }, face: { x: 558, y: 383 } },
  { name: "nasal opening", xray: { x: 542, y: 487 }, face: { x: 407, y: 463 } },
  { name: "teeth", xray: { x: 525, y: 671 }, face: { x: 353, y: 611 } },
  { name: "chin", xray: { x: 384, y: 808 }, face: { x: 229, y: 756 } },
  { name: "collarbones' notch", xray: { x: 627, y: 1097 }, face: { x: 492, y: 1014 } },
  { name: "far collarbone's end", xray: { x: 153, y: 962 }, face: { x: 61, y: 911 } },
  { name: "near collarbone's end", xray: { x: 1199, y: 1052 }, face: { x: 973, y: 1009 } },
];

/** The X-ray's dots on the face, in the face artwork's units. */
export interface XrayDots {
  count: number;
  x: Float32Array;
  y: Float32Array;
  /** Each radius already follows the fit's stretch and the face's shrinking edge. */
  r: Float32Array;
}

/** The dots of a grid of radius bytes, row by row, moved onto the face. Each radius grows
    with the fit's local stretch, so the dots stay round, and shrinks with the face's edge,
    so the X-ray breaks up where the face's dots do. A dot the edge removes is left out. */
export function unpackXrayDots(grid: Uint8Array): XrayDots {
  if (grid.length !== XRAY_GRID * XRAY_GRID) {
    throw new Error(`X-ray: expected ${XRAY_GRID * XRAY_GRID} cells, got ${grid.length}`);
  }
  const map = thinPlateSpline(
    XRAY_LANDMARKS.map(({ xray }) => xray),
    XRAY_LANDMARKS.map(({ face }) => face),
  );
  const x: number[] = [];
  const y: number[] = [];
  const r: number[] = [];
  for (let row = 0; row < XRAY_GRID; row++) {
    for (let column = 0; column < XRAY_GRID; column++) {
      const byte = grid[row * XRAY_GRID + column]!;
      if (byte === 0) continue;
      const center = { x: XRAY_STEP * column + 4, y: XRAY_STEP * row + 4 };
      const moved = map(center);
      const keep = clamp01(
        (faceEdgeKeep(moved.x, moved.y) - FACE_EDGE.minKeep) / (1 - FACE_EDGE.minKeep),
      );
      const radius =
        (byte / XRAY_RADIUS_LEVELS) * XRAY_MAX_RADIUS * radiusScale(map, center) * keep;
      if (radius <= 0) continue;
      x.push(moved.x);
      y.push(moved.y);
      r.push(radius);
    }
  }
  return {
    count: r.length,
    x: Float32Array.from(x),
    y: Float32Array.from(y),
    r: Float32Array.from(r),
  };
}
